import React from 'react'

import { t } from '../i18n/t'

/**
 * One broken screen must not take the whole app down mid-sale (T-018).
 *
 * A render error anywhere under a React root unmounts the ENTIRE tree — React
 * has done this since 16, deliberately, on the grounds that a corrupted UI is
 * worse than none. In this app that means one bad value on one screen leaves
 * the salesperson looking at a white page, with the order they were typing gone
 * from the DOM and no way back except reloading and starting again.
 *
 * A boundary per route turns that into: this screen failed, the sidebar still
 * works, go somewhere else. Draft autosave (T-012) means the order itself
 * survives.
 *
 * This is a class component because there is no hook equivalent —
 * componentDidCatch and getDerivedStateFromError have no hook form, and that is
 * still true in React 18.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { failed: false }
    }

    static getDerivedStateFromError() {
        return { failed: true }
    }

    componentDidCatch(error, info) {
        // No error-reporting service is wired up yet. The console is what
        // exists, and a render error that leaves no trace at all is far harder
        // to chase than a noisy one — this is the only place in the app where
        // console output is the point rather than a leftover.
        console.error('Screen failed to render:', error, info?.componentStack)
    }

    componentDidUpdate(prevProps) {
        // Without this, a failed boundary stays failed forever: navigating to
        // another screen swaps the children but leaves state.failed true, so
        // every subsequent route renders the error panel and the app appears
        // permanently broken. Resetting on a route change is what makes "go
        // somewhere else" actually work.
        if (this.state.failed && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ failed: false })
        }
    }

    render() {
        if (!this.state.failed) return this.props.children

        /*
         * ── WHY THIS IS NOT `card` AND `btn btn-primary` (reported 2026-07-27)
         *
         * It was, and the screenshot of the failure told the story: a white
         * panel with body text that could barely be read, and a purple button
         * that belongs to no palette in this application.
         *
         * Those are the template's Bootstrap classes, out of assets/css/style.css,
         * and that stylesheet has ONE theme. `.card` hardcodes a white
         * background. In dark mode the page around it is #17171a and the text
         * inherits --ink, which in dark resolves to near-white — so the message
         * was white-on-white. `.btn-primary` is --bs-primary, the template's
         * #723182, not this app's cobalt.
         *
         * The T-019 rule exists for exactly this: a component that reaches for
         * a theme-blind class is a component that is only correct in one theme,
         * and nothing catches it because it renders perfectly well in the other.
         *
         * It matters more here than almost anywhere. This panel is only ever
         * seen when something has already gone wrong, and it is the one screen
         * whose entire job is to be read.
         */
        return (
            <div className="screen-error" role="alert">
                <div className="screen-error__panel">
                    {/* h1, not h4. This replaces the whole page, so it IS the
                        page's heading — and h4 with no h1 above it leaves a
                        screen-reader user landing on a document with no title
                        at the moment they most need to know where they are. */}
                    <h1 className="screen-error__title">{t('errors.screenFailedTitle')}</h1>
                    <p className="screen-error__body">{t('errors.screenFailedBody')}</p>
                    <button
                        type="button"
                        className="ui-btn ui-btn--primary ui-focusable"
                        onClick={() => window.location.reload()}
                    >
                        {t('errors.reloadScreen')}
                    </button>
                </div>
            </div>
        )
    }
}
